import { useMemo } from 'react'
import { UserDTO } from '../models/User'
import { LoggedUser } from '../models/LoggedUser'
import * as roleUtils from '../../../common/utils/roleUtils'
import * as permissions from '../../../common/permission'

export const useUserRoles = (user: UserDTO | LoggedUser | undefined | null) => {
  return useMemo(() => {
    if (!user) {
      return {
        hasRole: () => false,
        hasAnyRole: () => false,
        hasAllRoles: () => false,
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,

        isPatient: false,
        isFamiliar: false,
        isMed: false,
        isAdmin: false,
        userType: null,

        canEditOwnData: false,
        canChangeOwnPassword: false,
        canCreateUser: false,
        canCreateFamiliar: false,
        canViewAnyUser: false,
        canEditAnyUser: false,
        canDeleteUser: false,
        canManageRoles: false,

        canAccessHome: false,
        canViewPatients: false,
        canViewStatistics: false,
        canAccessStatisticsMenu: false,
        canAccessForms: false,
        canViewProtocols: false,
        canManageProtocols: false,
        canApproveProtocols: false,
        canAssignProtocols: false,
        canAccessResources: false,

        canCreatePatient: false,
        canEditPatientData: false,
        canManagePatientCircle: false,
        canViewPatientDetails: false,
        canViewPatientStats: false,

        canEditPatients: false,
        canCreateForms: false,
        canAnswerForms: false,

        canLinkWearable: false,
        canViewWearable: false,

        canUploadFiles: false,
        canViewFiles: false,
        canDeleteFiles: false,
        canShareFiles: false,

        canViewOwnStats: false,
        canViewAnyStats: false,
      }
    }

    return {
      hasRole: (role: string) => roleUtils.hasRole(user, role),
      hasAnyRole: (roles: string[]) => roleUtils.hasAnyRole(user, roles),
      hasAllRoles: (roles: string[]) => roleUtils.hasAllRoles(user, roles),
      hasPermission: (permission: string) => permissions.hasPermission(user, permission),
      hasAnyPermission: (permissionsList: string[]) =>
        permissions.hasAnyPermission(user, permissionsList),
      hasAllPermissions: (permissionsList: string[]) =>
        permissions.hasAllPermissions(user, permissionsList),

      isPatient: roleUtils.isPatient(user),
      isFamiliar: roleUtils.isFamiliar(user),
      isMed: roleUtils.isMed(user),
      isAdmin: roleUtils.isAdmin(user),
      userType: roleUtils.getUserType(user),

      canEditOwnData: permissions.canEditOwnData(user),
      canChangeOwnPassword: permissions.canChangeOwnPassword(user),
      canCreateUser: permissions.canCreateUser(user),
      canCreateFamiliar: permissions.canCreateFamiliar(user),
      canViewAnyUser: permissions.canViewAnyUser(user),
      canEditAnyUser: permissions.canEditAnyUser(user),
      canDeleteUser: permissions.canDeleteUser(user),
      canManageRoles: permissions.canManageRoles(user),

      canAccessHome: permissions.canAccessHome(user),
      canViewPatients: permissions.canViewPatients(user),
      canViewStatistics: permissions.canViewStatistics(user),
      canAccessStatisticsMenu: permissions.canAccessStatisticsMenu(user),
      canAccessForms: permissions.canAccessForms(user),
      canViewProtocols: permissions.canViewProtocols(user),
      canManageProtocols: permissions.canManageProtocols(user),
      canApproveProtocols: permissions.canApproveProtocols(user),
      canAssignProtocols: permissions.canAssignProtocols(user),
      canAccessResources: permissions.canAccessResources(user),

      canCreatePatient: permissions.canCreatePatient(user),
      canEditPatientData: permissions.canEditPatientData(user),
      canManagePatientCircle: permissions.canManagePatientCircle(user),
      canViewPatientDetails: permissions.canViewPatientDetails(user),
      canViewPatientStats: permissions.canViewPatientStats(user),

      canEditPatients: permissions.canEditPatientData(user),
      canCreateForms: permissions.canCreateForms(user),
      canAnswerForms: permissions.canAnswerForms(user),

      canLinkWearable: permissions.canLinkWearable(user),
      canViewWearable: permissions.canViewWearable(user),

      canUploadFiles: permissions.canUploadFiles(user),
      canViewFiles: permissions.canViewFiles(user),
      canDeleteFiles: permissions.canDeleteFiles(user),
      canShareFiles: permissions.canShareFiles(user),

      canViewOwnStats: permissions.canViewOwnStats(user),
      canViewAnyStats: permissions.canViewAnyStats(user),
    }
  }, [user])
}
